import { useEffect, useState, type ElementType, type ReactElement } from 'react';
import { BookOpen, Building2, Clock3, Database, FlaskConical, Heart, Home, Layers3, Settings, ShieldCheck, Sparkles, Tag, Trophy } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import type { CategorySummary, SourceSummary } from '@ai-radar/shared';
import { api } from '../api';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

type NavItem = { to: string; label: string; icon: ElementType };
const primaryNav: NavItem[] = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/latest', label: 'Ultime', icon: Clock3 },
  { to: '/top-stories', label: 'Top stories', icon: Trophy },
  { to: '/open-source', label: 'Open Source', icon: Layers3 },
  { to: '/research', label: 'Research', icon: FlaskConical },
  { to: '/companies', label: 'Companies', icon: Building2 },
  { to: '/favorites', label: 'Preferiti', icon: Heart },
  { to: '/settings', label: 'Impostazioni', icon: Settings }
];

const linkClass = ({ isActive }: { isActive: boolean }): string => `group flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition ${isActive ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-panel-muted hover:text-ink'}`;

export function Sidebar({ open, onClose }: SidebarProps): ReactElement {
  const [sources, setSources] = useState<SourceSummary[]>([]);
  const [categories, setCategories] = useState<CategorySummary[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([api.sources(controller.signal), api.categories(controller.signal)])
      .then(([nextSources, nextCategories]) => { setSources(nextSources); setCategories(nextCategories); })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const closeOnMobile = (): void => { if (open) onClose(); };
  return (
    <>
      {open && <button aria-label="Chiudi menu" className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={onClose} />}
      <aside className={`${open ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-[68px] left-0 z-50 w-[270px] overflow-y-auto border-r border-line bg-canvas p-4 transition-transform lg:static lg:z-auto lg:block lg:w-auto lg:translate-x-0 lg:border-0 lg:bg-transparent lg:p-0`}>
        <div className="flex items-center justify-between px-2 pb-3 lg:hidden">
          <span className="eyebrow">Navigazione</span>
          <button className="text-xs text-muted" onClick={onClose}>Chiudi</button>
        </div>
        <nav aria-label="Navigazione principale" className="space-y-1">
          {primaryNav.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} className={linkClass} end={to === '/'} onClick={closeOnMobile}><Icon size={16} strokeWidth={1.8} /><span>{label}</span></NavLink>)}
        </nav>

        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between px-3"><span className="eyebrow">Fonti</span><Database size={13} className="text-subtle" /></div>
          <div className="space-y-1">
            {sources.length === 0 && <p className="px-3 text-xs leading-5 text-subtle">Le fonti appariranno dopo il primo avvio.</p>}
            {sources.slice(0, 12).map((source) => <Link key={source.id} to={`/search?source=${encodeURIComponent(source.slug)}`} onClick={closeOnMobile} className="flex items-center justify-between rounded-lg px-3 py-2 text-[12px] text-muted transition hover:bg-panel-muted hover:text-ink"><span className="flex min-w-0 items-center gap-2"><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${source.active ? 'bg-emerald-500' : 'bg-subtle'}`} /><span className="truncate">{source.name}</span></span>{typeof source.articleCount === 'number' && <span className="ml-2 text-[10px] text-subtle">{source.articleCount}</span>}</Link>)}
          </div>
        </div>

        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between px-3"><span className="eyebrow">Categorie</span><Tag size={13} className="text-subtle" /></div>
          <div className="space-y-1">
            {categories.length === 0 && <p className="px-3 text-xs leading-5 text-subtle">Nessuna categoria disponibile.</p>}
            {categories.slice(0, 14).map((category) => <Link key={category.id} to={`/search?category=${encodeURIComponent(category.slug)}`} onClick={closeOnMobile} className="flex items-center justify-between rounded-lg px-3 py-2 text-[12px] text-muted transition hover:bg-panel-muted hover:text-ink"><span className="truncate">{category.name}</span>{typeof category.articleCount === 'number' && <span className="ml-2 text-[10px] text-subtle">{category.articleCount}</span>}</Link>)}
          </div>
        </div>

        <div className="mt-8 border-t border-line pt-4">
          <NavLink to="/admin" className={linkClass} onClick={closeOnMobile}><ShieldCheck size={16} strokeWidth={1.8} /><span>Admin</span></NavLink>
          <Link to="/settings" onClick={closeOnMobile} className="mt-3 flex items-start gap-2 rounded-xl border border-line bg-panel p-3 text-[11px] leading-4 text-muted"><Sparkles size={15} className="mt-0.5 shrink-0 text-accent" /><span><strong className="font-semibold text-ink">AI Radar</strong><br />Il segnale prima del rumore.</span></Link>
        </div>
      </aside>
    </>
  );
}
